import { StateCreator } from 'zustand'
import { MedplumClient } from '@medplum/core'
import {
  BasePractitionerFragment,
  BasePractitionerRoleFragment,
  PractitionerData_GetPractitionersByNameQuery
} from 'generated/graphql'
import { formatPrimaryName } from 'utils/names'
import { getPractitionerTimezone } from 'fhir/Practitioner/helpers'
import { captureException, SentrySources } from 'utils/sentry'
import { getErrorInstance } from 'utils/helpers'
import { GET_PRACTITIONER_DATA_PRACTITIONER } from './queries/practitioner.query'
import { RootSlice } from './rootType'

type PractitionerInfo = {
  formattedPrimaryName: string | null
  timezone?: string
  resourceType: 'Practitioner'
}
export type PractitionerWithRoleInfoSchema =
  | ({
      PractitionerRoleList?: Array<BasePractitionerRoleFragment | null> | null
    } & BasePractitionerFragment &
      PractitionerInfo)
  | null

export type PractitionerListSchema = Array<PractitionerWithRoleInfoSchema>

export interface PractitionerSlice {
  practitioner: {
    idToPractitionerMapping: Record<string, PractitionerWithRoleInfoSchema>
    isLoading: boolean
    getPractitioner: (
      medplum: MedplumClient,
      id: string
    ) => Promise<PractitionerWithRoleInfoSchema | null>
    errorMessage: object | string | null
    update: (
      practitioners: PractitionerData_GetPractitionersByNameQuery['PractitionerList']
    ) => void
  }
}

export const createPractitionerSlice: StateCreator<
  RootSlice,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  PractitionerSlice
> = (set) => ({
  practitioner: {
    idToPractitionerMapping: {},
    isLoading: false,
    errorMessage: null,
    getPractitioner: async (medplum, id) => {
      try {
        set((state) => {
          state.practitioner.errorMessage = null
          state.practitioner.isLoading = true
        })

        const { data } = await medplum.graphql(GET_PRACTITIONER_DATA_PRACTITIONER, null, {
          id
        })

        const practitioner = data.Practitioner

        if (practitioner) {
          const formattedPrimaryName = formatPrimaryName(practitioner?.name)
          const timezone = getPractitionerTimezone(practitioner)
          const currObject = {
            ...practitioner,
            formattedPrimaryName,
            timezoneExtension: undefined,
            timezone
          }

          set((state) => {
            state.practitioner.idToPractitionerMapping[id] = currObject
            state.practitioner.isLoading = false
          })
        }

        return null
      } catch (error) {
        captureException(getErrorInstance(error), {
          tags: { source: SentrySources.PRACTITIONER_SLICE },
          extras: { params: { id } }
        })
        set((state) => {
          state.practitioner.errorMessage = error?.message || 'Error getting practitioner data.'
          state.practitioner.isLoading = false
        })

        return null
      }
    },
    update: (practitioners: PractitionerData_GetPractitionersByNameQuery['PractitionerList']) => {
      if (!practitioners) return
      const parsedMapping = practitioners.reduce((acc, curr) => {
        if (!curr) return acc
        const formattedPrimaryName = formatPrimaryName(curr?.name)
        const timezone = getPractitionerTimezone(curr)
        const parsedPractitioner = {
          ...curr,
          formattedPrimaryName,
          timezoneExtension: undefined,
          timezone
        }
        acc[parsedPractitioner.id as string] = parsedPractitioner
        return acc
      }, {})

      set((state) => {
        state.practitioner.idToPractitionerMapping = {
          ...parsedMapping
        }
      })
    }
  }
})
